import { useState, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@learn-me/ui";
import { Volume2, Trash2, Loader2, RefreshCw, Headphones } from "lucide-react";
import { adminApi } from "../../api/adminApi";
import toast from "react-hot-toast";

interface AudioAdminControlsProps {
  sectionId: string;
  hasAudio: boolean;
  onAudioChanged?: () => void;
}

/**
 * Compact admin controls for managing section audio.
 * Shows below the AudioPlayer or as a generator when no audio exists.
 */
export function AudioAdminControls({
  sectionId,
  hasAudio,
  onAudioChanged,
}: AudioAdminControlsProps) {
  const queryClient = useQueryClient();
  const [selectedVoice, setSelectedVoice] = useState("es-MX-DaliaNeural");

  // Fetch available voices
  const { data: voices = [] } = useQuery({
    queryKey: ["tts-voices"],
    queryFn: () => adminApi.getTTSVoices(),
    staleTime: Infinity,
  });

  // Generate audio mutation
  const generateMutation = useMutation({
    mutationFn: () =>
      adminApi.generateSectionAudio(sectionId, { voice: selectedVoice }),
    onSuccess: () => {
      toast.success("Audio generado");
      queryClient.invalidateQueries({ queryKey: ["admin-chapter-sections"] });
      onAudioChanged?.();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al generar audio",
      );
    },
  });

  // Delete audio mutation
  const deleteMutation = useMutation({
    mutationFn: () => adminApi.deleteSectionAudio(sectionId),
    onSuccess: () => {
      toast.success("Audio eliminado");
      queryClient.invalidateQueries({ queryKey: ["admin-chapter-sections"] });
      onAudioChanged?.();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al eliminar audio",
      );
    },
  });

  const isLoading = generateMutation.isPending || deleteMutation.isPending;

  // When audio exists - show regenerate/delete controls
  if (hasAudio) {
    return (
      <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
        <select
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
          className="flex-1 p-1.5 text-xs border border-purple-200 rounded bg-white"
          disabled={isLoading}
        >
          {voices.map((voice) => (
            <option key={voice.id} value={voice.id}>
              {voice.name}
            </option>
          ))}
        </select>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
          onClick={() => generateMutation.mutate()}
          disabled={isLoading}
        >
          {generateMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3 mr-1" />
          )}
          Regenerar
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs border-red-200 text-red-600 hover:bg-red-50"
          onClick={() => deleteMutation.mutate()}
          disabled={isLoading}
        >
          {deleteMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Trash2 className="h-3 w-3" />
          )}
        </Button>
      </div>
    );
  }

  // When no audio - show generator
  return (
    <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Headphones className="h-4 w-4 text-purple-600" />
        </div>
        <span className="text-sm font-medium text-purple-800">
          Generar Audio TTS
        </span>
      </div>

      <div className="flex items-center gap-2">
        <select
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
          className="flex-1 p-2 text-sm border border-purple-200 rounded-lg bg-white"
          disabled={isLoading}
        >
          {voices.map((voice) => (
            <option key={voice.id} value={voice.id}>
              {voice.name} - {voice.description}
            </option>
          ))}
        </select>

        <Button
          type="button"
          size="sm"
          className="bg-purple-600 hover:bg-purple-700 text-white"
          onClick={() => generateMutation.mutate()}
          disabled={isLoading}
        >
          {generateMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Volume2 className="h-4 w-4 mr-1" />
          )}
          Generar
        </Button>
      </div>
    </div>
  );
}
